import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { fetchUsers, fetchUserById, fetchUserCards, fetchUserInstallments, fetchUserAddress, updateUserInfo, updateUserAddress } from '../redux/slices/userSlice';
import { activityService } from '../services/activityService';
import { useSocket } from '../context/SocketContext';
import { parseActivityDate } from '../utils/activityPayload';
import {
  getUserActivityDisplayDetail,
  getUserActivityDisplayTitle,
} from '../utils/activityUserDetailLabels';
import {
  ArrowLeft,
  Edit2,
  Save,
  CreditCard,
  Activity,
  Lock,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Clock,
  Wallet,
  CheckCircle,
  XCircle,
  AlertCircle,
  Bell,
  BellOff,
  UserPlus,
  Wifi,
  WifiOff
} from 'lucide-react';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux'tan kullanıcı listesi ve cache'i al
  const { users, selectedUser, userDetailsCache, cardsLoading, installmentsLoading, addressLoading, loading, error } = useSelector((state) => state.users);

  const [isEditing, setIsEditing] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const { socket } = useSocket();
  
  // Form state'leri
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    telefon: '',
    email: '',
    adres: ''
  });

  const splitFullName = (fullName) => {
    const normalized = (fullName || '').toString().trim().replace(/\s+/g, ' ');
    if (!normalized) return { firstName: '', lastName: '' };
    const parts = normalized.split(' ');
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  };

  // Aktivite state'leri
  const [userActivities, setUserActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState(null);

  // Detay sayfasına her girişte sayfayı en üste al.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  // Sayfa yenilendiğinde kullanıcı listesini yükle (eğer yoksa)
  useEffect(() => {
    // Kullanıcı listesi boşsa ve yüklenmiyorsa çek
    if (users.length === 0 && !loading) {
      dispatch(fetchUsers({ force: false }));
    }
  }, [dispatch, users.length, loading]);

  // URL'den gelen id ile kullanıcıyı çek (sayfa yenilendiğinde de çalışır)
  useEffect(() => {
    const is404Error = error && (
      typeof error === 'string' ? error.includes('404') : 
      error?.status === 404 || 
      error?.response?.status === 404 ||
      (typeof error === 'object' && error?.message?.includes('404'))
    );
    if (is404Error) return;

    if (id) {
      const isCorrectUser = selectedUser &&
        (String(selectedUser.id) === String(id) || String(selectedUser.tc) === String(id));

      if (!isCorrectUser) {
        dispatch(fetchUserById(id)).catch(() => {});
      }
    }
  }, [id, dispatch, selectedUser, error]);

  // 404 hatası kontrolü
  const is404Error = error && (
    typeof error === 'string' ? error.includes('404') : 
    error?.status === 404 || 
    error?.response?.status === 404 ||
    (typeof error === 'object' && error?.message?.includes('404'))
  );

  // Kullanıcı bilgileri: selectedUser sadece URL'deki id ile eşleşiyorsa kullan
  const selectedMatchesRoute = selectedUser &&
    (String(selectedUser.id) === String(id) || String(selectedUser.tc) === String(id));
  const foundUser = selectedMatchesRoute
    ? selectedUser
    : users.find((u) => String(u.id) === String(id) || String(u.tc) === String(id));
  const user = foundUser || {
    name: '-',
    tc: '-',
    email: '-',
    phone: '-',
    registerDate: '-',
    loyaltyPoints: 0,
    segment: '-',
    status: 'active',
    address: '-',
  };

  // Bu müşterinin cache'deki verileri
  const currentUserCache = userDetailsCache[user.tc] || {};
  const userCards = currentUserCache.cards || [];
  const userAddress = currentUserCache.address ?? null;

  // Kullanıcı kartlarını ve adresini çek (cache'de yoksa)
  useEffect(() => {
    if (user.tc && user.tc !== '-') {
      const cache = userDetailsCache[user.tc] || {};

      // Kartlar cache'de yoksa çek
      if (!cache.cards && !cardsLoading) {
        dispatch(fetchUserCards(user.tc));
      }

      // Adres cache'de yoksa çek (tek seferlik, müşteriye özel)
      if (cache.address === undefined && !addressLoading) {
        dispatch(fetchUserAddress({ identityNum: user.tc })).catch((error) => {
          // 500 veya diğer hatalar için sessizce handle et
          // fetchUserAddress zaten null döndürüyor, burada sadece promise'i handle ediyoruz
        });
      }
    }
  }, [dispatch, user.tc, userDetailsCache, cardsLoading, addressLoading]);

  // Kullanıcı bilgileri geldiğinde form state'ini initialize et
  // Adres bilgisi - Cache'den veya fallback
  const normalizedAddress = userAddress ?? (user.address && user.address !== '-' ? user.address : '') ?? '';
  const addressInputValue = !isEditing && !formData.adres ? '-' : formData.adres;

  useEffect(() => {
    if (user && user.tc !== '-' && !isEditing) {
      const nameParts =
        user.firstName || user.lastName
          ? {
              firstName: (user.firstName || '').toString().trim(),
              lastName: (user.lastName || '').toString().trim(),
            }
          : splitFullName(user.name);
      setFormData({
        firstName: nameParts.firstName,
        lastName: nameParts.lastName,
        telefon: user.phone || '',
        email: user.email || '',
        adres: normalizedAddress || ''
      });
    }
  }, [user, normalizedAddress, isEditing]);

  // Kullanıcının kartları (cache'den veya fallback)
  const kozakartList = userCards.length > 0 ? userCards : [
    {
      id: 1,
      cardNumber: '-',
      cardType: 'Kart bilgisi yok',
      city: '-',
      balance: 0,
      limit: 0,
      status: 'inactive',
      createdDate: '-',
      lastUsed: '-',
    },
  ];

  const selectedCard = kozakartList[selectedCardIndex] || kozakartList[0];

  // Seçili kartın taksitleri (her kart için ayrı)
  const allInstallments = currentUserCache.installments || {};
  const userInstallments = selectedCard.cardNumber && allInstallments[selectedCard.cardNumber]
    ? allInstallments[selectedCard.cardNumber]
    : [];

  // Seçili kart değiştiğinde taksitleri çek (cache'de yoksa)
  useEffect(() => {
    if (selectedCard && selectedCard.cardNumber && selectedCard.cardNumber !== '-' && user.tc && user.tc !== '-') {
      const cache = userDetailsCache[user.tc] || {};

      // Bu kartın taksitleri cache'de var mı? (kart bazlı kontrol)
      const cardInstallments = cache.installments?.[selectedCard.cardNumber];

      if (cardInstallments === undefined && !installmentsLoading) {
        dispatch(fetchUserInstallments({ currAccCode: selectedCard.cardNumber }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedCard?.cardNumber, user.tc, installmentsLoading]);

  // Form input değişikliklerini handle et
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Kaydet butonuna basıldığında
  const handleSave = async () => {
    if (isEditing) {
      try {
        const firstName = (formData.firstName || '').trim();
        const lastName = (formData.lastName || '').trim();
        const telefon = (formData.telefon || '').trim();
        const email = (formData.email || '').trim();

        if (!user.tc || user.tc === '-') {
          toast.error('TC kimlik numarası bulunamadı.');
          return;
        }
        if (!telefon) {
          toast.error('Telefon zorunludur.');
          return;
        }
        if (!firstName) {
          toast.error('Ad zorunludur.');
          return;
        }
        if (!lastName) {
          toast.error('Soyad zorunludur.');
          return;
        }

        await dispatch(updateUserInfo({
          tc: user.tc,
          firstName,
          lastName,
          telefon,
          email
        })).unwrap();

        const trimmedAddress = (formData.adres || '').trim();
        const currentAddress = (normalizedAddress || '').trim();
        if (trimmedAddress !== currentAddress) {
          await dispatch(updateUserAddress({
            identityNum: user.tc,
            addressData: { Address: trimmedAddress }
          })).unwrap();
        }

        toast.success('Kullanıcı bilgileri başarıyla güncellendi');
        setIsEditing(false);
      } catch (error) {
        toast.error(typeof error === 'string' ? error : 'Güncelleme sırasında bir hata oluştu');
      }
    } else {
      setIsEditing(true);
    }
  };

  /**
   * Kullanıcı aktivitelerini getir
   */
  const fetchUserActivities = useCallback(async () => {
    if (!user.tc || user.tc === '-') return;

    try {
      setActivitiesLoading(true);
      setActivitiesError(null);
      
      // userId olarak TC veya ID kullan
      const userId = user.id || user.tc;
      const result = await activityService.getActivities({ 
        userId: userId,
        limit: 20 
      });

      if (result.success) {
        // Backend'den ne gelirse gelsin, sadece ilk 20 aktiviteyi göster
        const activities = result.data || [];
        setUserActivities(activities.slice(0, 20));
      } else {
        setActivitiesError(result.error || 'Aktiviteler yüklenemedi');
        setUserActivities([]);
      }
    } catch (err) {
      setActivitiesError('Aktiviteler yüklenirken bir hata oluştu');
      setUserActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, [user.tc, user.id]);

  /**
   * WebSocket'ten gelen aktivite güncellemesini işle
   */
  useEffect(() => {
    if (!socket || !user.tc || user.tc === '-') return;

    const handleActivityUpdate = (data) => {
      if (data && data.event) {
        const activityUserId = data.event.userId || data.event.data?.tcNo || data.event.data?.userId;
        const currentUserId = user.id || user.tc;
        
        // Bu kullanıcıya ait aktivite ise ekle
        if (String(activityUserId) === String(currentUserId)) {
          const newActivity = {
            id: data.event.id || `activity_${Date.now()}`,
            userId: data.event.userId,
            eventType: data.event.eventType,
            eventCategory: data.event.eventCategory,
            timestamp: data.event.timestamp || new Date().toISOString(),
            data: data.event.data || {},
          };

          setUserActivities((prev) => [newActivity, ...prev].slice(0, 20));
        }
      }
    };

    socket.on('activity-update', handleActivityUpdate);

    return () => {
      socket.off('activity-update', handleActivityUpdate);
    };
  }, [socket, user.tc, user.id]);

  /**
   * Kullanıcı aktivitelerini yükle
   */
  useEffect(() => {
    if (user.tc && user.tc !== '-') {
      fetchUserActivities();
    }
  }, [user.tc, fetchUserActivities]);

  /**
   * Event tipine göre ikon döndür
   */
  const getEventIcon = (eventType) => {
    const key = (eventType || '').toString().trim().toLowerCase();
    const iconMap = {
      'user_token_registered': <Bell size={16} className="text-blue-600" />,
      'user_token_unregistered': <BellOff size={16} className="text-gray-600" />,
      'user_register': <UserPlus size={16} className="text-green-600" />,
      'user_registered': <UserPlus size={16} className="text-green-600" />,
      'user_login': <User size={16} className="text-indigo-600" />,
      'login': <User size={16} className="text-indigo-600" />,
      'signin': <User size={16} className="text-indigo-600" />,
      'user_connection_established': <Wifi size={16} className="text-green-600" />,
      'user_connection_closed': <WifiOff size={16} className="text-red-600" />,
      'session_start': <Wifi size={16} className="text-green-600" />,
      'session_end': <WifiOff size={16} className="text-red-600" />,
      'notification_sent': <Bell size={16} className="text-purple-600" />,
      'notification_read': <Bell size={16} className="text-blue-600" />,
      'payment_method_selected': <CreditCard size={16} className="text-emerald-600" />,
      'qr_code_generated': <CheckCircle size={16} className="text-teal-600" />,
      'qr_code_generate': <CheckCircle size={16} className="text-teal-600" />,
      'campaign_viewed': <Activity size={16} className="text-orange-600" />,
      'campaign_detail_view': <Activity size={16} className="text-orange-600" />,
      'campaign_clicked': <CreditCard size={16} className="text-pink-600" />,
      'limit_increase_requested': <CreditCard size={16} className="text-yellow-600" />,
      'limit_increase_approved': <CheckCircle size={16} className="text-green-600" />,
      'limit_increase_rejected': <XCircle size={16} className="text-red-600" />,
      'screen_view': <Activity size={16} className="text-cyan-600" />,
    };
    return iconMap[key] || <Activity size={16} className="text-gray-600" />;
  };

  const getActivityTimestamp = (activity) =>
    activity?.timestamp ||
    activity?.createdAt ||
    activity?.created_at ||
    activity?.eventDate ||
    activity?.date ||
    null;

  /**
   * Zamanı formatla
   */
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Bilinmiyor';
    
    try {
      const date = parseActivityDate(timestamp);
      if (!date) return 'Bilinmiyor';
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);

      if (diffInSeconds < 0) {
        return date.toLocaleString('tr-TR', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
      if (diffInSeconds < 60) return 'Az önce';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} dk önce`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} saat önce`;
      
      return date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Geçersiz tarih';
    }
  };

  /**
   * Tarihi formatla (DD.MM.YYYY)
   */
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    
    try {
      const date = parseActivityDate(timestamp);
      if (!date) return '-';
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return '-';
    }
  };

  /**
   * Saati formatla (HH:MM)
   */
  const formatHour = (timestamp) => {
    if (!timestamp) return '-';
    
    try {
      const date = parseActivityDate(timestamp);
      if (!date) return '-';
      return date.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return '-';
    }
  };

  // 404 hatası durumunda özel mesaj göster
  if (is404Error && !loading) {
    return (
      <div className="space-y-5">
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/kullanicilar')}
              className="p-3 hover:bg-gray-50 rounded-2xl transition-all border border-gray-100 hover:border-gray-200 text-gray-500 hover:text-gray-900 group"
            >
              <ArrowLeft className="w-6 h-6 group-hover:-translate-x-1 transition-transform" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Kullanıcı Bulunamadı</h1>
              <p className="text-gray-500 mt-2 text-lg">Aradığınız kullanıcı sistemde bulunamadı</p>
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-red-900 mb-2">Kullanıcı ID: {id}</h3>
                <p className="text-red-700">
                  Bu ID'ye sahip bir kullanıcı bulunamadı. Kullanıcı silinmiş olabilir veya ID yanlış olabilir.
                </p>
                <button
                  onClick={() => navigate('/kullanicilar')}
                  className="mt-3 px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold"
                >
                  Kullanıcı Listesine Dön
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/kullanicilar')}
              className="p-3 hover:bg-gray-50 rounded-2xl transition-all border border-gray-100 hover:border-gray-200 text-gray-500 hover:text-gray-900 group"
            >
              <ArrowLeft className="w-6 h-6 group-hover:-translate-x-1 transition-transform" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Kullanıcı Detayı</h1>
              <p className="text-gray-500 mt-2 text-lg">Kullanıcı bilgilerini görüntüleyin ve düzenleyin</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={loading}
            className={`flex items-center gap-2 px-8 py-3.5 rounded-2xl transition-all font-semibold shadow-lg ${isEditing
              ? 'bg-green-500 text-white hover:bg-green-600 shadow-green-200'
              : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? '⏳ İşleniyor...' : isEditing ? <><Save className="w-5 h-5" /> Kaydet</> : <><Edit2 className="w-5 h-5" /> Düzenle</>}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Basic Info */}
        <div className="lg:col-span-2 space-y-5">
          {/* Temel Bilgiler */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 bg-purple-50 rounded-2xl">
                <User className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Temel Bilgiler</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <CreditCard size={14} className="text-gray-400" />
                  TC Kimlik No
                </label>
                <input
                  type="text"
                  value={user.tc}
                  disabled
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 font-mono cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <User size={14} className="text-gray-400" />
                  Ad
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-xl transition-all ${isEditing
                    ? 'bg-white border-blue-200 focus:ring-4 focus:ring-blue-50/50 focus:border-blue-400'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                    }`}
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <User size={14} className="text-gray-400" />
                  Soyad
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-xl transition-all ${isEditing
                    ? 'bg-white border-blue-200 focus:ring-4 focus:ring-blue-50/50 focus:border-blue-400'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                    }`}
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <Mail size={14} className="text-gray-400" />
                  E-posta
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-xl transition-all ${isEditing
                    ? 'bg-white border-blue-200 focus:ring-4 focus:ring-blue-50/50 focus:border-blue-400'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                    }`}
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <Phone size={14} className="text-gray-400" />
                  Telefon
                </label>
                <input
                  type="tel"
                  name="telefon"
                  value={formData.telefon}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-xl transition-all ${isEditing
                    ? 'bg-white border-blue-200 focus:ring-4 focus:ring-blue-50/50 focus:border-blue-400'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                    }`}
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <Calendar size={14} className="text-gray-400" />
                  Kayıt Tarihi
                </label>
                <input
                  type="text"
                  value={user.registerDate}
                  disabled
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <MapPin size={14} className="text-gray-400" />
                  Adres
                </label>
                <textarea
                  name="adres"
                  value={addressInputValue}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  rows="3"
                  className={`w-full px-4 py-3 border rounded-xl transition-all ${isEditing
                    ? 'bg-white border-blue-200 focus:ring-4 focus:ring-blue-50/50 focus:border-blue-400'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                    }`}
                  placeholder={addressLoading ? 'Adres yükleniyor...' : 'Adres bilgisi'}
                />
                {userAddress === null && !addressLoading && (
                  <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-50 p-2 rounded-lg w-fit">
                    <AlertCircle size={14} />
                    Bu müşteri için adres bilgisi sistemde bulunmuyor
                  </div>
                )}
              </div>
            </div>

            {/* Password Reset */}
            <div className="mt-5 pt-8 border-t border-gray-100">
              <button className="flex items-center gap-3 px-4 py-3 bg-orange-50 text-orange-700 rounded-2xl hover:bg-orange-100 transition-colors w-full md:w-auto justify-center font-bold border border-orange-100 hover:border-orange-200">
                <Lock size={20} className="text-orange-500" /> Parola Sıfırlama Linki Gönder
              </button>
            </div>
          </div>

          {/* Kozakart */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 bg-blue-50 rounded-2xl">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">
                Kozakart ({kozakartList.length} Kart)
              </h2>
            </div>

            {/* Kart Seçici - Mağaza Lokasyonlarına Göre */}
            <div className="flex gap-4 mb-5 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
              {kozakartList.map((card, index) => (
                <button
                  key={card.id}
                  onClick={() => setSelectedCardIndex(index)}
                  className={`flex-shrink-0 p-4 rounded-2xl border-2 transition-all min-w-[240px] text-left group relative overflow-hidden ${selectedCardIndex === index
                    ? 'border-blue-500 bg-blue-50/50'
                    : 'border-gray-100 hover:border-blue-200 bg-gray-50/50'
                    }`}
                >
                  <div className="flex items-start mb-3">
                    <div className="p-2 bg-white rounded-xl shadow-sm">
                      <MapPin className={`w-5 h-5 ${selectedCardIndex === index ? 'text-blue-500' : 'text-gray-400'}`} />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className={`text-xs font-bold uppercase tracking-wider ${selectedCardIndex === index ? 'text-blue-500' : 'text-gray-500'
                      }`}>
                      {card.city}
                    </p>
                    <p className="font-bold text-gray-900">{card.cardType}</p>
                    <p className="text-sm text-gray-500 font-mono tracking-wide">{card.cardNumber}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Seçili Kart Detayları */}
              <div className="grid grid-cols-1 gap-4">
                <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500 text-sm font-medium">Toplam Limit</span>
                    <Wallet className="text-gray-400 w-5 h-5" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    ₺{selectedCard.limit.toLocaleString('tr-TR')}
                  </p>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1 p-5 bg-red-50 rounded-2xl border border-red-100">
                    <p className="text-red-600 text-xs font-bold uppercase tracking-wider mb-1">Borç</p>
                    <p className="text-xl font-bold text-red-700">
                      ₺{selectedCard.balance.toLocaleString('tr-TR')}
                    </p>
                    <div className="w-full bg-red-100 rounded-full h-1.5 mt-3">
                      <div
                        className="bg-red-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${Math.min((selectedCard.balance / selectedCard.limit) * 100, 100)}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex-1 p-5 bg-green-50 rounded-2xl border border-green-100">
                    <p className="text-green-600 text-xs font-bold uppercase tracking-wider mb-1">Kullanılabilir</p>
                    <p className="text-xl font-bold text-green-700">
                      ₺{(selectedCard.limit - selectedCard.balance).toLocaleString('tr-TR')}
                    </p>
                </div>
              </div>
            </div>
          </div>

          {/* Taksit Planı */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 bg-green-50 rounded-2xl">
                <Wallet className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Taksit Planı</h2>
            </div>

            {installmentsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : userInstallments.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-100 border-dashed">
                  <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">Taksit bilgisi bulunamadı</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Ödenen Taksitler */}
                  {userInstallments.filter(t => t.OdemeDurumu === 'Ödendi' || t.Paid).length > 0 && (
                    <div className="bg-green-50/50 rounded-2xl border border-green-100 p-4">
                      <p className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
                        <CheckCircle size={16} /> Ödenen Taksitler
                      </p>
                      <div className="space-y-2">
                        {userInstallments
                          .filter(t => t.OdemeDurumu === 'Ödendi' || t.Paid)
                          .map((installment, index) => (
                            <div key={index} className="p-4 bg-white rounded-xl border border-green-100/50 flex items-center justify-between shadow-sm">
                              <div>
                                <p className="text-sm text-gray-600 mb-0.5 font-bold">₺{(installment.Tutar || installment.Amount || 0).toLocaleString('tr-TR')}</p>
                                <p className="text-xs text-gray-400 flex items-center gap-1">
                                  <Clock size={10} />
                                  {installment.OdemeTarihi || installment.PaymentDate || '-'}
                                </p>
                              </div>
                              <span className="text-xs px-2.5 py-1 bg-green-100 text-green-700 rounded-full font-bold">Ödendi</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Ödenmemiş Taksitler */}
                  {userInstallments.filter(t => !(t.OdemeDurumu === 'Ödendi' || t.Paid)).length > 0 && (
                    <div className="bg-orange-50/50 rounded-2xl border border-orange-100 p-4">
                      <p className="text-sm font-bold text-orange-700 mb-3 flex items-center gap-2">
                        <AlertCircle size={16} /> Ödeme Bekleyenler
                      </p>
                      <div className="space-y-2">
                        {userInstallments
                          .filter(t => !(t.OdemeDurumu === 'Ödendi' || t.Paid))
                          .map((installment, index) => {
                            // Tarih formatını kontrol et - eğer dd.mm.yyyy formatındaysa parse et
                            const parseDate = (dateStr) => {
                              if (!dateStr || dateStr === '-') return null;
                              const parts = dateStr.split('.');
                              if (parts.length === 3) {
                                return new Date(parts[2], parts[1] - 1, parts[0]);
                              }
                              return new Date(dateStr);
                            };

                            const dueDate = parseDate(installment.VadeTarihi || installment.DueDate);
                            const isOverdue = dueDate && dueDate < new Date();

                            return (
                              <div
                                key={index}
                                className={`p-4 bg-white rounded-xl border flex items-center justify-between shadow-sm transition-all ${isOverdue ? 'border-red-100 ring-1 ring-red-50' : 'border-orange-100 hover:border-orange-200'
                                  }`}
                              >
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-sm font-bold text-gray-900">
                                      ₺{(installment.Tutar || installment.Amount || 0).toLocaleString('tr-TR')}
                                    </p>
                                    {(installment.VadeFarki || installment.InterestAmount) > 0 && (
                                      <span className="text-xs text-gray-400">+ ₺{(installment.VadeFarki || installment.InterestAmount).toLocaleString('tr-TR')} vade farkı</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 flex items-center gap-1 font-medium">
                                    <Calendar size={12} />
                                    Son Ödeme: {installment.VadeTarihi || installment.DueDate || '-'}
                                  </p>
                                </div>
                                <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                                  }`}>
                                  {isOverdue ? 'Gecikmiş' : 'Beklemede'}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>

        {/* Right Column - Stats */}
        <div className="space-y-5">
          {/* Stats Cards */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Activity className="w-5 h-5 text-gray-400" />
              İstatistikler
            </h3>

            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl border border-amber-100 transition-transform hover:scale-[1.02]">
                <p className="text-sm font-bold text-amber-800/70 mb-1 uppercase tracking-wider">Sadakat Puanı</p>
                <div className="flex items-end justify-between">
                  <p className="text-2xl font-black text-amber-900 tracking-tight">{user.loyaltyPoints}</p>
                  <div className="p-2 bg-white/50 rounded-full">
                    <Activity size={18} className="text-amber-600" />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl border border-blue-100 transition-transform hover:scale-[1.02]">
                <p className="text-sm font-bold text-blue-800/70 mb-1 uppercase tracking-wider">Segment</p>
                <div className="flex items-end justify-between">
                  <p className="text-2xl font-black text-blue-900 tracking-tight">{user.segment}</p>
                  <div className="p-2 bg-white/50 rounded-full">
                    <User size={18} className="text-blue-600" />
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Kullanıcı İşlem Geçmişi */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 sticky top-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gray-50 rounded-xl">
                <Clock className="text-gray-500 w-5 h-5" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Son Aktiviteler</h2>
            </div>

            {activitiesLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : activitiesError ? (
              <div className="text-center py-12 bg-yellow-50 rounded-2xl border border-yellow-100">
                <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
                <p className="text-yellow-700 font-medium">{activitiesError}</p>
              </div>
            ) : userActivities.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-100 border-dashed">
                <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">Henüz aktivite bulunmuyor</p>
              </div>
            ) : (
              <div className="relative pl-4 space-y-4 before:absolute before:left-4 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100">
                {userActivities.map((activity, index) => {
                  const activityDetail = getUserActivityDisplayDetail(activity);
                  return (
                  <div key={activity.id || index} className="relative pl-6">
                    <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white ring-4 ring-gray-50 bg-blue-500"></div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-gray-400">
                          {getEventIcon(activity.eventType)}
                        </div>
                        <p className="font-bold text-gray-900 text-sm">
                          {getUserActivityDisplayTitle(activity)}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 font-medium flex items-center gap-1">
                        {formatDate(getActivityTimestamp(activity))} <span className="w-1 h-1 rounded-full bg-gray-300"></span> {formatHour(getActivityTimestamp(activity))}
                        <span className="ml-2 text-gray-400">({formatTime(getActivityTimestamp(activity))})</span>
                      </p>
                      {activityDetail && (
                        <p className="text-xs text-gray-600 mt-1.5">{activityDetail}</p>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default UserDetail;

